import WebviewComponentAdaptor from "@shared/components/application/other/WebviewComponentAdaptor/WebviewComponentAdaptor.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Button from "@ui-kit/form/Button/Button.web";
import { postMessageToReactNativeWebView } from "@shared/utils/webview";
import { useTranslation } from "@shared/utils/internationalization";
import Link from "next/link";

type ViewOrderButtonProps = {
  isLoggedIn: boolean;
  myOrderUrl: string;
  stagingPageUrl: string;
  transactionId: string;
};

/**
 * A button component that allows users to view their order details.
 * The component adapts its behavior based on the platform:
 * - In web environments: Renders a link to the appropriate order page
 * - In React Native webview: Sends a message to the native app to handle the action
 *
 * Note: This component is only displayed on small screens (below 'sm' breakpoint).
 *
 */
export default function ViewOrderButton(
  props: ViewOrderButtonProps
): JSX.Element {
  return (
    <Box display={{ base: "flex", sm: "none" }}>
      <WebviewComponentAdaptor
        webComponent={<ViewOrderButton.WebComponent {...props} />}
        webviewComponent={<ViewOrderButton.WebviewComponent {...props} />}
      />
    </Box>
  );
}

ViewOrderButton.WebviewComponent = function WebviewComponent({
  isLoggedIn,
  transactionId,
  stagingPageUrl,
}: Omit<ViewOrderButtonProps, "myOrderUrl">) {
  const postOrderMessage = () =>
    postMessageToReactNativeWebView({
      action: "VIEW_ORDER",
      payload: isLoggedIn
        ? { transactionId }
        : { guestCheckoutOrderUrl: stagingPageUrl },
    });

  return <ViewOrderButton.ButtonComponent onClick={postOrderMessage} />;
};

ViewOrderButton.WebComponent = function WebComponent({
  isLoggedIn,
  myOrderUrl,
  stagingPageUrl,
}: Omit<ViewOrderButtonProps, "transactionId">) {
  return (
    <Link
      href={isLoggedIn ? myOrderUrl : stagingPageUrl}
      passHref
      style={{ width: "100%" }}
      target="_blank"
    >
      <ViewOrderButton.ButtonComponent />
    </Link>
  );
};

ViewOrderButton.ButtonComponent = function ButtonComponent({
  onClick,
}: {
  onClick?: () => void;
}) {
  const { t } = useTranslation("checkout");

  return (
    <Button.Outline
      as="span"
      width="100%"
      data-testid="order-status-view-order"
      onClick={onClick}
    >
      {t("View order")}
    </Button.Outline>
  );
};
